import React, { useMemo, useRef, useState } from 'react';
import {
  Box, Button, Dialog, IconButton, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
  useMediaQuery, useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import CustomDatePicker from '../form-components/CustomDatePicker';
import ConfirmModal from '../form-components/ConfirmModal';
import LocalText from '../common/LocalText';
import QuizQuestionEditor from './QuizQuestionEditor';
import QuizPreview from './QuizPreview';
import { useLocalization } from '../../context/LocalizationContext';
import { C, btnBase, ctaNavy } from '../../theme/adminTokens';
import { addDays, todayEastern } from '../../utils/dateUtils';
import {
  MODAL_MODES, NAME_MAX_LENGTH, SCHEDULE_TYPES,
} from '../../constants/gameSchedule';
import {
  buildSavePayload, fmtDateOnly, formatPeriodRange, isoDate, mapToValues, newForm, newQuestion, nextFreeCustomPeriod,
  nextFreeReferenceDate, quizToForm, recomputeLinkedAnnouncement, validateForm,
} from '../../utils/gameSchedule';

const TITLE_ID = 'schedule-game-modal-title';

const thinScrollbar = {
  scrollbarWidth: 'thin',
  scrollbarColor: `${C.border} transparent`,
  '&::-webkit-scrollbar': { width: 6, height: 6 },
  '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
  '&::-webkit-scrollbar-thumb': { backgroundColor: C.border, borderRadius: 4 },
  '&::-webkit-scrollbar-thumb:hover': { backgroundColor: C.muted },
};

const labelSx = { fontSize: 11, color: C.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, mb: 0.5 };

const inputSx = {
  '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: 13, bgcolor: '#ffffff' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: C.border },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: C.navy },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: C.navy },
};

const toggleGroupSx = {
  bgcolor: '#ffffff',
  border: `1px solid ${C.border}`,
  borderRadius: '10px',
  p: '3px',
  '& .MuiToggleButtonGroup-grouped': {
    border: 0,
    borderRadius: '8px !important',
    px: 2,
    py: 0.5,
    fontSize: 12.5,
    fontWeight: 700,
    textTransform: 'none',
    color: C.muted,
    '&:hover': { bgcolor: '#eff6ff' },
    '&.Mui-selected': { ...ctaNavy },
  },
};

const MODE_LABEL = {
  [MODAL_MODES.CREATE]: null,
  [MODAL_MODES.EDIT]: 'Edit draft',
  [MODAL_MODES.PROPOSE]: 'Propose edit',
  [MODAL_MODES.RESUBMIT]: 'Edit & resubmit',
  [MODAL_MODES.VIEW]: 'View',
};

const SUBMIT_LABEL = {
  [MODAL_MODES.CREATE]: 'Create (as Draft)',
  [MODAL_MODES.EDIT]: 'Save Changes',
  [MODAL_MODES.PROPOSE]: 'Submit for Approval',
  [MODAL_MODES.RESUBMIT]: 'Resubmit for Approval',
};

const SAVING_LABEL = {
  [MODAL_MODES.PROPOSE]: 'Submitting…',
  [MODAL_MODES.RESUBMIT]: 'Resubmitting…',
};

// The language the editor opens in: English when the space has it, else the first language of the space.
const initialLanguage = (availableLocales) =>
  (availableLocales?.includes('en_us') ? 'en_us' : (availableLocales?.[0] ?? 'en_us'));

/**
 * The Schedule Game popup (plan 13.3): stage 1 "Build" (schedule details and period on the left, the selected question
 * on the right), stage 2 "Preview" (how fans will see it). One popup for every way in:
 *   create   — a new draft; the last button reads "Create (as Draft)"
 *   edit     — a draft is changed directly
 *   propose  — a live quiz that has not started: the edit becomes a proposal for approval
 *   resubmit — a rejected proposal is edited and sent again
 *   view     — read-only
 * When a live quiz is edited (propose / resubmit) and a live announcement is linked to it, the announcement's dates are
 * recomputed from all its quizzes (earliest start .. latest end). Only when those dates change does submitting first ask for
 * the announcement date (prefilled, editable, not before the announcement's new end).
 * `baselineQuiz` is the live quiz the change is measured against (the proposal's snapshot when resubmitting a rejected one
 * would show the proposed dates, not the live ones); it defaults to `initialQuiz`.
 * Mount it only while it is open (the form state lives here and starts fresh each time). `onSave(payload)` does the
 * request; if it throws, the popup stays open with everything typed.
 */
export default function ScheduleGameModal({
  mode = MODAL_MODES.CREATE,
  gameName = 'Quiz',
  pointsPerCorrect = null,
  initialQuiz = null,
  baselineQuiz = null,
  siblings = [],
  onClose,
  onSave,
}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { availableLocales } = useLocalization();

  const readOnly = mode === MODAL_MODES.VIEW;
  const today = useMemo(() => todayEastern(), []);

  // A new quiz opens on the first free week, like a new Custom one opens on the first free period.
  const [form, setForm] = useState(() => {
    if (initialQuiz) return quizToForm(initialQuiz);
    const fresh = newForm(today);
    return { ...fresh, referenceDate: nextFreeReferenceDate(fresh.scheduleType, siblings, fresh.referenceDate) };
  });
  const [stage, setStage] = useState('build');
  const [selected, setSelected] = useState(0);
  const [lang, setLang] = useState(() => initialLanguage(availableLocales));
  const [saving, setSaving] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const editorRef = useRef(null);

  // The live announcement linked to the quiz being edited (none for a new quiz or a draft), and the announcement date asked for
  // in the popup: filled in with the current one.
  const baseline = baselineQuiz ?? initialQuiz;
  const linkedAnnouncement = baseline?.linkedAnnouncement ?? null;
  const [confirmAnnouncement, setConfirmAnnouncement] = useState(false);
  // null until the marketer touches it: then the date follows the current announcement, even if the live quiz arrives late.
  const [announcementDateEdit, setAnnouncementDateEdit] = useState(null);
  const announcementDate = announcementDateEdit ?? isoDate(linkedAnnouncement?.announcementDate);

  // What the form would send when it was opened: a change to it is what "unsaved changes" means.
  const initialPayload = useRef(null);
  if (initialPayload.current === null) initialPayload.current = JSON.stringify(buildSavePayload(form));
  const dirty = JSON.stringify(buildSavePayload(form)) !== initialPayload.current;

  const validation = useMemo(() => validateForm(form, { today, siblings }), [form, today, siblings]);
  const custom = form.scheduleType === 'custom';
  const period = validation.period ?? null;

  // The dates the linked announcement would get (this quiz's new period with the announcement's other quizzes): the
  // prize day is asked for only when they differ from the saved ones. Winners already granted freeze the quiz.
  const edits = !readOnly && (mode === MODAL_MODES.PROPOSE || mode === MODAL_MODES.RESUBMIT);
  const announcementDates = recomputeLinkedAnnouncement(period, linkedAnnouncement);
  const asksAnnouncement = edits && Boolean(announcementDates?.changed);
  const winnersLocked = edits && Boolean(linkedAnnouncement?.hasWinners);

  // Every language of the space, plus any language the quiz already has text in (a language removed from the space
  // later must not hide existing translations).
  const localeOptions = useMemo(() => {
    const used = new Set();
    form.questions.forEach((q) => {
      Object.keys(q.text ?? {}).forEach((l) => used.add(l));
      q.options.forEach((o) => Object.keys(o.text ?? {}).forEach((l) => used.add(l)));
    });
    const all = [...(availableLocales ?? [])];
    used.forEach((l) => { if (!all.includes(l)) all.push(l); });
    return all.length ? all : ['en_us'];
  }, [form.questions, availableLocales]);

  const safeSelected = Math.min(selected, Math.max(0, form.questions.length - 1));
  const question = form.questions[safeSelected];
  const completeCount = validation.perQuestion.filter((issues) => issues.length === 0).length;

  const setField = (patch) => setForm((f) => ({ ...f, ...patch }));

  const handleTypeChange = (next) => {
    if (next === form.scheduleType) return;
    setForm((f) => {
      const patch = { scheduleType: next };
      if (next === 'custom' && (!f.startDate || !f.endDate)) {
        const free = nextFreeCustomPeriod(siblings, today);
        patch.startDate = free.start;
        patch.endDate = free.end;
      }
      if (next !== 'custom') patch.referenceDate = nextFreeReferenceDate(next, siblings, f.referenceDate || f.startDate || today);
      return { ...f, ...patch };
    });
  };

  // A new start date keeps the end date when it still follows it; otherwise the end moves to a week-long period.
  const handleStartChange = (e) => {
    const start = e.target.value;
    setForm((f) => ({ ...f, startDate: start, endDate: f.endDate && f.endDate >= start ? f.endDate : addDays(start, 6) }));
  };

  const updateQuestion = (index, next) =>
    setForm((f) => ({ ...f, questions: f.questions.map((q, i) => (i === index ? next : q)) }));

  const addQuestion = () => {
    setForm((f) => ({ ...f, questions: [...f.questions, newQuestion()] }));
    setSelected(form.questions.length);
    // On a phone the editor sits below the list: bring it into view.
    setTimeout(() => editorRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' }), 0);
  };

  const removeQuestion = (index) => {
    setForm((f) => ({ ...f, questions: f.questions.filter((_, i) => i !== index) }));
    setSelected((s) => Math.max(0, Math.min(s, form.questions.length - 2)));
  };

  const selectQuestion = (index) => {
    setSelected(index);
    setTimeout(() => editorRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' }), 0);
  };

  const requestClose = () => {
    if (saving) return;
    if (dirty && !readOnly) setConfirmClose(true);
    else onClose();
  };

  // `extra` carries what the popup collected (the announcement date of the linked announcement).
  const submit = async (extra = {}) => {
    setSaving(true);
    try {
      await onSave({ ...buildSavePayload(form), ...extra });
    } catch {
      // The page shows the error (the API interceptor toasts it); everything typed stays for another try.
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = () => {
    if (readOnly || !validation.valid || saving || winnersLocked) return;
    if (asksAnnouncement) {
      setConfirmAnnouncement(true);
      return;
    }
    submit();
  };

  // The announcement date is the day prizes are planned to be granted: not before the announcement ends.
  const announcementDateTooEarly = Boolean(announcementDate && announcementDates && announcementDate < announcementDates.end);

  const confirmAnnouncementUpdate = () => {
    if (!announcementDate || announcementDateTooEarly) return;
    setConfirmAnnouncement(false);
    submit({ linkedAnnouncement: { announcementDate } });
  };

  const pointsText = pointsPerCorrect == null
    ? 'Each correct answer = 1 point (the default of this space).'
    : `Each correct answer = ${pointsPerCorrect} point${pointsPerCorrect === 1 ? '' : 's'} (set for this space).`;

  const stageLabel = stage === 'build' ? 'Build' : 'Preview';
  const modeLabel = MODE_LABEL[mode];
  const subtitle = [gameName, modeLabel, stageLabel].filter(Boolean).join(' · ');

  const firstProblem = (winnersLocked ? `Winners were already granted for the announcement "${linkedAnnouncement.name}" that uses this quiz, so it can't be changed.` : null)
    || validation.nameError
    || validation.dateError
    || (validation.overlap ? 'The period overlaps another quiz' : null)
    || validation.questionsError
    || (validation.perQuestion.some((i) => i.length > 0) ? `${form.questions.length - completeCount} question${form.questions.length - completeCount === 1 ? '' : 's'} still need attention` : null);

  const rangeText = period ? formatPeriodRange(period.start, period.end) : '';
  const otherQuizCount = Math.max(0, (linkedAnnouncement?.quizCount ?? 1) - 1);

  return (
    <>
      <Dialog
        open
        onClose={requestClose}
        fullScreen={fullScreen}
        maxWidth={false}
        aria-labelledby={TITLE_ID}
        slotProps={{
          backdrop: { sx: { bgcolor: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(2px)' } },
          paper: {
            'data-testid': 'schedule-game-modal',
            sx: {
              width: '100%', maxWidth: 1320, height: fullScreen ? '100%' : 'min(90vh, 800px)', maxHeight: fullScreen ? '100%' : '90vh',
              m: { xs: 0, sm: 2 }, borderRadius: fullScreen ? 0 : '16px', overflow: 'hidden', display: 'flex', flexDirection: 'column',
            },
          },
        }}
      >
        {/* Header */}
        <Box sx={{ px: { xs: 2, sm: 3 }, py: { xs: 1.75, sm: 2 }, bgcolor: C.navy, color: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexShrink: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: '12px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)', color: '#7dd3fc' }}>
              <EventAvailableOutlinedIcon sx={{ fontSize: 22 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography id={TITLE_ID} component="h2" noWrap sx={{ fontSize: { xs: 16, sm: 18 }, fontWeight: 700, letterSpacing: '-0.01em' }}>
                <LocalText>Schedule Game</LocalText>
              </Typography>
              <Typography noWrap sx={{ fontSize: 11, color: '#cbd5e1', mt: 0.25 }}>{subtitle}</Typography>
            </Box>
          </Box>
          <IconButton
            onClick={requestClose}
            aria-label="Close"
            sx={{ width: 32, height: 32, flexShrink: 0, bgcolor: 'rgba(255,255,255,0.1)', color: '#e2e8f0', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)', color: '#ffffff' } }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        {/* Body */}
        <Box sx={{ flex: 1, minHeight: 0, overflowY: stage === 'build' ? { xs: 'auto', md: 'hidden' } : 'auto', bgcolor: '#f8fafc', ...thinScrollbar }}>
          {stage === 'build' ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: '380px minmax(0, 1fr)' }, height: { md: '100%' } }}>
              {/* Left pane: schedule details + the question list */}
              <Box sx={{ p: { xs: 2, sm: 3 }, display: 'flex', flexDirection: 'column', gap: 2, borderRight: { md: `1px solid ${C.border}` }, borderBottom: { xs: `1px solid ${C.border}`, md: 0 }, overflowY: { md: 'auto' }, bgcolor: '#ffffff', ...thinScrollbar }}>
                <Typography sx={{ fontSize: 12, fontWeight: 800, color: C.navy, letterSpacing: 0.6, textTransform: 'uppercase' }}>
                  <LocalText>Schedule details</LocalText>
                </Typography>

                <Box>
                  <Typography component="label" htmlFor="quiz-name" sx={{ ...labelSx, display: 'block' }}><LocalText>Name *</LocalText></Typography>
                  <TextField
                    id="quiz-name"
                    fullWidth
                    size="small"
                    value={form.name}
                    onChange={(e) => setField({ name: e.target.value })}
                    disabled={readOnly}
                    placeholder="e.g. Week 44 Quiz"
                    inputProps={{ maxLength: NAME_MAX_LENGTH }}
                    error={!readOnly && Boolean(validation.nameError)}
                    helperText={!readOnly && validation.nameError ? validation.nameError : ''}
                    sx={inputSx}
                  />
                </Box>

                <Box>
                  <Typography sx={labelSx}><LocalText>Period *</LocalText></Typography>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={form.scheduleType}
                    onChange={(_e, next) => { if (next !== null) handleTypeChange(next); }}
                    disabled={readOnly}
                    sx={toggleGroupSx}
                    aria-label="quiz period"
                  >
                    {SCHEDULE_TYPES.map((t) => (
                      <ToggleButton key={t.value} value={t.value} aria-label={t.label}>
                        <LocalText>{t.label}</LocalText>
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </Box>

                {!custom && (
                  <Box>
                    <Typography sx={labelSx}>
                      <LocalText>{form.scheduleType === 'weekly' ? 'Week of * (pick any day in it)' : 'Month of * (pick any day in it)'}</LocalText>
                    </Typography>
                    <CustomDatePicker
                      value={form.referenceDate}
                      min={readOnly ? undefined : today}
                      onChange={(e) => setField({ referenceDate: e.target.value })}
                      placeholder="Select a date"
                      disabled={readOnly}
                      enableYearNav
                      fullWidth
                      isError={!readOnly && Boolean(validation.dateError)}
                      highlightWeek={form.scheduleType === 'weekly'}
                      highlightMonth={form.scheduleType === 'monthly'}
                      // Red = the days another quiz really holds. A week / month that touches one can still be picked: the
                      // picker then shows those days dark red inside it, and the overlap message below names the quiz.
                      disabledRanges={siblings}
                      clashTitle="Already has a quiz"
                    />
                  </Box>
                )}

                <Box sx={{ display: 'flex', gap: 1.5, flexDirection: { xs: 'column', sm: 'row', md: 'column', lg: 'row' } }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={labelSx}><LocalText>Start Date</LocalText></Typography>
                    <CustomDatePicker
                      value={custom ? form.startDate : (period?.start ?? '')}
                      min={custom && !readOnly ? today : undefined}
                      max={custom ? (form.endDate || undefined) : undefined}
                      onChange={handleStartChange}
                      placeholder="Start date"
                      disabled={readOnly || !custom}
                      enableYearNav
                      fullWidth
                      isError={custom && !readOnly && Boolean(validation.dateError)}
                      highlightRange={custom}
                      rangeAnchor={custom ? (form.endDate || null) : null}
                      disabledRanges={custom ? siblings : []}
                      clashTitle="Already has a quiz"
                    />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={labelSx}><LocalText>End Date</LocalText></Typography>
                    <CustomDatePicker
                      value={custom ? form.endDate : (period?.end ?? '')}
                      min={custom ? (form.startDate || undefined) : undefined}
                      onChange={(e) => setField({ endDate: e.target.value })}
                      placeholder="End date"
                      disabled={readOnly || !custom}
                      enableYearNav
                      fullWidth
                      isError={custom && !readOnly && Boolean(validation.dateError)}
                      highlightRange={custom}
                      rangeAnchor={custom ? (form.startDate || null) : null}
                      disabledRanges={custom ? siblings : []}
                      clashTitle="Already has a quiz"
                    />
                  </Box>
                </Box>
                {!custom && (
                  <Typography sx={{ fontSize: 11, color: C.muted, mt: -1 }}>
                    <LocalText>Start and End Date are filled in from the date above and can't be edited.</LocalText>
                  </Typography>
                )}
                {!readOnly && validation.dateError && (
                  <Typography role="alert" sx={{ fontSize: 11.5, color: '#ef4444', mt: -1 }}>
                    <LocalText>{validation.dateError}</LocalText>
                  </Typography>
                )}
                {!readOnly && validation.overlap && (
                  <Typography role="alert" sx={{ fontSize: 11.5, color: '#ef4444', mt: -1 }}>
                    <LocalText>{`This period overlaps "${validation.overlap.name}" (${formatPeriodRange(validation.overlap.startStr, validation.overlap.endStr)}). Choose a non-overlapping period.`}</LocalText>
                  </Typography>
                )}

                <Typography sx={{ fontSize: 12, color: C.muted }}><LocalText>{pointsText}</LocalText></Typography>

                <Box sx={{ borderTop: `1px solid ${C.border}`, pt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 800, color: C.navy, letterSpacing: 0.6, textTransform: 'uppercase' }}>
                    <LocalText>Questions</LocalText> ({completeCount}/{form.questions.length} <LocalText>complete</LocalText>)
                  </Typography>
                  {validation.questionsError && !readOnly && (
                    <Typography role="alert" sx={{ fontSize: 11.5, color: '#ef4444' }}><LocalText>{validation.questionsError}</LocalText></Typography>
                  )}
                  <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }} aria-label="Questions">
                    {form.questions.map((q, i) => {
                      const issues = validation.perQuestion[i] ?? [];
                      const complete = issues.length === 0;
                      const active = i === safeSelected;
                      const first = mapToValues(q.text)[0]?.value;
                      const own = String(q.text?.[lang] ?? '').trim();
                      return (
                        <Box component="li" key={q._key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Tooltip title={complete ? '' : issues.join(' · ')} placement="right">
                            <Box
                              component="button"
                              type="button"
                              onClick={() => selectQuestion(i)}
                              aria-current={active ? 'true' : undefined}
                              aria-label={`Question ${i + 1}${complete ? '' : ' needs attention'}`}
                              sx={{
                                flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 1, px: 1.25, py: 0.875, textAlign: 'left',
                                fontFamily: 'inherit', cursor: 'pointer', borderRadius: '8px',
                                border: `1px solid ${active ? C.navy : C.border}`, bgcolor: active ? '#eff6ff' : '#ffffff',
                                '&:hover': { bgcolor: active ? '#eff6ff' : '#f8fafc' },
                              }}
                            >
                              <Box component="span" sx={{ fontSize: 12, fontWeight: 800, color: C.muted, minWidth: 16 }}>{i + 1}</Box>
                              {complete
                                ? <CheckCircleOutlineIcon sx={{ fontSize: 17, color: '#16a34a', flexShrink: 0 }} />
                                : <ErrorOutlineIcon sx={{ fontSize: 17, color: '#d97706', flexShrink: 0 }} />}
                              <Box component="span" sx={{ fontSize: 12.5, color: '#1e293b', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: active ? 700 : 400 }}>
                                {own || first || <Box component="span" sx={{ color: '#94a3b8' }}>(empty question)</Box>}
                              </Box>
                            </Box>
                          </Tooltip>
                          {!readOnly && (
                            <Tooltip title={form.questions.length <= 1 ? 'A quiz needs at least one question' : 'Remove question'}>
                              <span>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => removeQuestion(i)}
                                  disabled={form.questions.length <= 1}
                                  aria-label={`Remove question ${i + 1}`}
                                >
                                  <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                  {!readOnly && (
                    <Button
                      size="small"
                      startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                      onClick={addQuestion}
                      sx={{ ...btnBase, alignSelf: 'flex-start', color: C.navy, fontSize: 12 }}
                    >
                      <LocalText>Add question</LocalText>
                    </Button>
                  )}
                </Box>
              </Box>

              {/* Right pane: the selected question */}
              <Box ref={editorRef} sx={{ p: { xs: 2, sm: 3 }, overflowY: { md: 'auto' }, minWidth: 0, ...thinScrollbar }}>
                {question && (
                  <QuizQuestionEditor
                    key={question._key}
                    question={question}
                    index={safeSelected}
                    total={form.questions.length}
                    onChange={(next) => updateQuestion(safeSelected, next)}
                    readOnly={readOnly}
                    issues={validation.perQuestion[safeSelected] ?? []}
                  />
                )}
              </Box>
            </Box>
          ) : (
            <Box sx={{ p: { xs: 2, sm: 3 } }}>
              <QuizPreview form={form} period={period} lang={lang} localeOptions={localeOptions} onLangChange={setLang} />
            </Box>
          )}
        </Box>

        {/* Footer */}
        <Box sx={{ px: { xs: 2, sm: 3 }, py: 1.5, bgcolor: '#f8fafc', borderTop: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap', flexShrink: 0 }}>
          {stage === 'build' ? (
            <>
              <Typography role="status" sx={{ fontSize: 12, color: firstProblem && !readOnly ? '#b45309' : C.muted, flex: '1 1 200px', minWidth: 0 }}>
                {readOnly
                  ? rangeText
                  : (firstProblem ? <LocalText>{firstProblem}</LocalText> : <LocalText>Ready to preview.</LocalText>)}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5, width: { xs: '100%', sm: 'auto' }, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={requestClose}
                  sx={{ ...btnBase, borderColor: C.border, color: C.navy, flex: { xs: 1, sm: 'none' }, '&:hover': { borderColor: C.navy, bgcolor: '#eff6ff' } }}
                >
                  <LocalText>{readOnly ? 'Close' : 'Cancel'}</LocalText>
                </Button>
                <Button
                  variant="contained"
                  onClick={() => setStage('preview')}
                  disabled={(!validation.valid && !readOnly) || winnersLocked}
                  sx={{ ...btnBase, ...ctaNavy, flex: { xs: 1, sm: 'none' } }}
                >
                  <LocalText>Preview →</LocalText>
                </Button>
              </Box>
            </>
          ) : (
            <>
              <Button
                variant="outlined"
                onClick={() => setStage('build')}
                sx={{ ...btnBase, borderColor: C.border, color: C.navy, '&:hover': { borderColor: C.navy, bgcolor: '#eff6ff' } }}
              >
                <LocalText>{readOnly ? '← Back' : '← Back to edit'}</LocalText>
              </Button>
              {!readOnly && (
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={!validation.valid || saving}
                  sx={{ ...btnBase, ...ctaNavy }}
                >
                  <LocalText>{saving ? (SAVING_LABEL[mode] ?? 'Saving…') : SUBMIT_LABEL[mode]}</LocalText>
                </Button>
              )}
            </>
          )}
        </Box>
      </Dialog>

      {confirmAnnouncement && linkedAnnouncement && period && announcementDates && (
        <Dialog
          open
          onClose={() => setConfirmAnnouncement(false)}
          maxWidth="xs"
          fullWidth
          aria-labelledby="linked-announcement-title"
          slotProps={{ paper: { 'data-testid': 'linked-announcement-dialog', sx: { borderRadius: '14px' } } }}
        >
          <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography id="linked-announcement-title" component="h3" sx={{ fontSize: 16, fontWeight: 700, color: C.navy }}>
              <LocalText>Linked announcement will change</LocalText>
            </Typography>
            <Typography sx={{ fontSize: 13, color: '#334155' }}>
              <LocalText>
                {otherQuizCount > 0
                  ? `The announcement "${linkedAnnouncement.name}" also has ${otherQuizCount === 1 ? 'one other quiz' : `${otherQuizCount} other quizzes`}, so its dates are recalculated from all of them once the change is approved.`
                  : `The announcement "${linkedAnnouncement.name}" is linked to this scheduled game, so it takes the new dates once the change is approved.`}
              </LocalText>
            </Typography>
            <Box sx={{ border: `1px solid ${C.border}`, borderRadius: '10px', p: 1.5, display: 'flex', flexDirection: 'column', gap: 0.75, bgcolor: '#f8fafc' }}>
              <Typography sx={{ fontSize: 12.5 }}>
                <b><LocalText>Scheduled game</LocalText></b>{': '}
                {formatPeriodRange(baseline.startDate, baseline.endDate)} <LocalText>to</LocalText> {formatPeriodRange(period.start, period.end)}
              </Typography>
              <Typography sx={{ fontSize: 12.5 }}>
                <b><LocalText>Announcement</LocalText></b>{': '}
                {formatPeriodRange(linkedAnnouncement.startDate, linkedAnnouncement.endDate)} <LocalText>to</LocalText> {formatPeriodRange(announcementDates.start, announcementDates.end)}
              </Typography>
            </Box>
            <Box>
              <Typography sx={{ ...labelSx, display: 'block' }}><LocalText>Announcement date *</LocalText></Typography>
              <CustomDatePicker
                value={announcementDate}
                min={announcementDates.end}
                onChange={(e) => setAnnouncementDateEdit(e.target.value)}
                placeholder="Announcement date"
                isError={announcementDateTooEarly}
                enableYearNav
                fullWidth
              />
              {announcementDateTooEarly ? (
                <Typography role="alert" sx={{ fontSize: 11, color: '#ef4444', mt: 0.75 }}>
                  <LocalText>{`Announcement date must be on or after the announcement's end date (${fmtDateOnly(announcementDates.end)}). Please choose a new date.`}</LocalText>
                </Typography>
              ) : (
                <Typography sx={{ fontSize: 11, color: C.muted, mt: 0.75 }}>
                  <LocalText>This is the day prizes are planned to be granted. It is filled in with the current date: update it if it should change.</LocalText>
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 0.5 }}>
              <Button
                variant="outlined"
                onClick={() => setConfirmAnnouncement(false)}
                sx={{ ...btnBase, borderColor: C.border, color: C.navy, '&:hover': { borderColor: C.navy, bgcolor: '#eff6ff' } }}
              >
                <LocalText>Back</LocalText>
              </Button>
              <Button
                variant="contained"
                onClick={confirmAnnouncementUpdate}
                disabled={!announcementDate || announcementDateTooEarly || saving}
                sx={{ ...btnBase, ...ctaNavy }}
              >
                <LocalText>Update</LocalText>
              </Button>
            </Box>
          </Box>
        </Dialog>
      )}

      <ConfirmModal
        open={confirmClose}
        onCancel={() => setConfirmClose(false)}
        onConfirm={() => { setConfirmClose(false); onClose(); }}
        severity="warning"
        title="Discard changes?"
        message="You have unsaved changes in this quiz."
        subtitle="If you close it now, they are lost."
        confirmLabel="Discard"
        cancelLabel="Keep editing"
      />
    </>
  );
}
